import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CandidateForm, {
  CandidateFormRef,
  CandidateFormValues,
} from './CandidateForm';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';

type CreateCandidateProps = {
  isOpen: boolean;
  toggle: () => void;
};

const CreateCandidateModal: React.FC<CreateCandidateProps> = ({
  isOpen,
  toggle,
}: CreateCandidateProps) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();
  const {
    candidateList: { queryParams },
  } = useSelector((state: AppState) => state.prospectStore);

  const formRef = useRef<CandidateFormRef>(null);

  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (values: CandidateFormValues) => {
    setIsCreating(true);
    try {
      const params = {
        DOB: values.dob ? moment(values.dob).toISOString() : null,
        agencyId: values.agency?.value ?? null,
        // Business type only belongs to the self-employed models, and neither
        // bureau nor Ireland has one
        businessType:
          !isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(values.paymentModel?.value as string)
            ? (values.businessType?.value ?? null)
            : null,
        companyPaymentId: values.paymentCompany?.value ?? null,
        email: values.email ?? null,
        forename: values.forename ?? null,
        payFrequency: values.payFrequency ?? null,
        paymentModel: values.paymentModel?.value ?? null,
        phone: values.phoneNumber || null,
        status: 'PENDING',
        surname: values.surname ?? null,
        title: values.title?.value ?? null,
      };
      if (isUK) {
        params.paymentModel = PAYMENT_MODEL_ENUM.PAYEE;
      }
      // unwrap() so a rejected request throws — without it the thunk resolves
      // and the modal closes as if the create had succeeded
      await dispatch(prospectActions.createCandidate(params)).unwrap();
      await dispatch(prospectActions.getCandidateList({ ...queryParams }));
      toggle();
      formRef.current?.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['prospect.addNewCandidate']}
      isOpen={isOpen}
      toggle={toggle}
      // Unmount the form on close so nothing typed before carries over — a
      // plain resetFields() leaves AppSelectSync showing its last selection
      destroyOnClose
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          disabled: isCreating,
          loading: isCreating,
        },
      ]}
    >
      <CandidateForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateCandidateModal;
